import Phaser from 'phaser';
import StartupScene from './scenes/startup-scene.js';
import { useCanvas, isMac, isSafariBrowser, isChrome } from './config.js';
import { initNative } from './native/bootstrap.js';
import { startLatencyProbe } from './audio/latency-probe.js';
import { getTestOptions, installTestModeRuntime } from './testing/test-mode.js';
import { initMenus } from './ui/menus.js';

const testOptions = getTestOptions();

// No-ops unless ?latency=1 (see latency-probe.js). Started here rather than in
// music-engine.js so the overlay is visible from the start screen — the engine
// is not imported until the player begins, and a probe you cannot see before
// then is easy to mistake for a broken one.
startLatencyProbe();

// Native shell setup (status bar, stale service workers). No-ops on the web, and
// deliberately not awaited — nothing in the boot path depends on it, and the
// game should never wait on a plugin round-trip to show its first frame.
initNative();

// ============================================
// ANALYTICS
// ============================================

// Helper function for safe GA event tracking
window.trackEvent = function(eventName, parameters) {
  if (!testOptions.enabled && typeof window.gtag === 'function') {
    window.gtag('event', eventName, parameters);
  }
};

// Track session start
if (!testOptions.enabled) window.addEventListener('load', () => {
  window.trackEvent('session_start', {
    platform: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
    screen_size: `${window.innerWidth}x${window.innerHeight}`,
    orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
  });
});

// Track control type (will be updated when controls are used)
window.controlType = 'unknown';

// Track time engagement automatically (GA4 does this but we can enhance it)
if (!testOptions.enabled) {
  let engagementTime = 0;
  setInterval(() => {
    if (document.hasFocus()) {
      engagementTime += 10;
      if (engagementTime === 60) {
        window.trackEvent('engagement_1min', {});
      } else if (engagementTime === 300) {
        window.trackEvent('engagement_5min', {});
      } else if (engagementTime === 600) {
        window.trackEvent('engagement_10min', {});
      }
    }
  }, 10000);
}

// ============================================
// PHASER INITIALIZATION
// ============================================

// Log which renderer we're using
console.log('Platform:', isMac ? 'Mac' : 'Other', '| Browser:', isSafariBrowser ? 'Safari' : isChrome ? 'Chrome' : 'Other');
console.log('Renderer:', useCanvas ? 'Canvas (Mac optimization)' : 'WebGL/Auto');

// Initialize Phaser game with proper scale configuration
// Exposed on window so the DOM menus can reach scene references
const game = window.game = new Phaser.Game({
  type: testOptions.enabled || useCanvas ? Phaser.CANVAS : Phaser.AUTO,
  parent: 'gameContainer',
  scene: [StartupScene],

  render: {
    powerPreference: 'high-performance',
    antialias: false,
    pixelArt: false
  },

  scale: {
    mode: Phaser.Scale.RESIZE,
    parent: 'gameContainer',
    width: window.visualViewport ? window.visualViewport.width : window.innerWidth,
    height: window.visualViewport ? window.visualViewport.height : window.innerHeight,
    autoCenter: Phaser.Scale.CENTER_BOTH
  }
});

installTestModeRuntime(game, Phaser);

// ============================================
// RESIZE HANDLERS
// ============================================

// Wait for game to be fully initialized before setting up resize handlers
setTimeout(() => {
  if (game && game.scale) {
    // Proper way to listen for Phaser resize events
    game.scale.on('resize', (gameSize) => {
      // Main stays registered after quitting to the title screen; only resize it while it runs.
      const mainScene = game.scene.getScene('Main');
      if (mainScene?.scene.isActive() && mainScene.resize) {
        mainScene.resize(gameSize);
      }
    });

    // Window resize listener as backup
    window.addEventListener('resize', () => {
      if (game && game.scale) {
        game.scale.refresh();
      }
    });

    // Handle visual viewport changes (mobile browser UI showing/hiding)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        if (game && game.scale) {
          game.scale.refresh();
        }
      });
    }

    // Check orientation changes
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        if (game && game.scale) {
          game.scale.refresh();
        }
      }, 100);
    });
  }
}, 500);

// ============================================
// MENUS (pause/settings; music controls bind later, after a user gesture)
// ============================================

initMenus();

// ============================================
// SERVICE WORKER (PWA)
// ============================================

// Skipped in the native build: the bundle is already on local disk, `../sw.js`
// resolves above the WebView's webroot anyway, and a second cache layer can
// serve stale assets across an app update. `__NATIVE_BUILD__` is a build-time
// constant, so this whole block is eliminated from the native bundle.
if (!__NATIVE_BUILD__ && !testOptions.enabled && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('../sw.js')
      .then(registration => {
      })
      .catch(error => {
      });
  });
}
